import { useMemo, useState } from "react";
import { Button } from "../../components/ui/Button";
import { useMembersIndex } from "../team/useMembersIndex";
import { useLeadsQuery } from "../leads/api";
import { describeActiveFilters } from "../leads/leadFilterLabels";
import type { LeadFilters } from "../leads/useLeadFilters";
import { buildLeadsExportUrl, triggerDownload, type LeadExportParams } from "./api";

// Exports are bounded to a one-week window on lead creation date.
const MAX_RANGE_DAYS = 6; // inclusive span: 7 calendar days

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDaysISO(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function diffDays(fromISO: string, toISO: string): number {
  return Math.round(
    (Date.parse(`${toISO}T00:00:00.000Z`) - Date.parse(`${fromISO}T00:00:00.000Z`)) /
      86_400_000
  );
}

// Seed the picker from the dashboard's date filter when present, clamping to a
// 7-day window; otherwise default to the most recent week.
function initialRange(filters: LeadFilters): { from: string; to: string } {
  const today = todayISO();
  let from = filters.from || "";
  let to = filters.to || "";
  if (from && to) {
    if (diffDays(from, to) < 0) from = addDaysISO(to, -MAX_RANGE_DAYS);
    if (diffDays(from, to) > MAX_RANGE_DAYS) from = addDaysISO(to, -MAX_RANGE_DAYS);
  } else if (to && !from) {
    from = addDaysISO(to, -MAX_RANGE_DAYS);
  } else if (from && !to) {
    to = addDaysISO(from, MAX_RANGE_DAYS);
  } else {
    to = today;
    from = addDaysISO(today, -MAX_RANGE_DAYS);
  }
  return { from, to };
}

interface Props {
  workspaceSlug: string | null;
  filters: LeadFilters;
  onClose: () => void;
}

export function ExportLeadsModal({ workspaceSlug, filters, onClose }: Props) {
  const seed = useMemo(() => initialRange(filters), [filters]);
  const [from, setFrom] = useState(seed.from);
  const [to, setTo] = useState(seed.to);

  const membersIndex = useMembersIndex();
  const activeFilters = useMemo(
    () => describeActiveFilters(filters, membersIndex.resolveName),
    [filters, membersIndex]
  );

  const span = from && to ? diffDays(from, to) : null;
  const rangeError =
    !from || !to
      ? "Pick a start and end date."
      : span! < 0
        ? "The start date must be on or before the end date."
        : span! > MAX_RANGE_DAYS
          ? "The range can’t be longer than one week."
          : null;

  const exportParams: LeadExportParams = {
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.source ? { source: filters.source } : {}),
    ...(filters.assignee ? { assignedTo: filters.assignee } : {}),
    ...(filters.campaign ? { campaign: filters.campaign } : {}),
    ...(filters.city.length ? { city: filters.city } : {}),
    ...(filters.state.length ? { state: filters.state } : {}),
    ...(filters.q ? { q: filters.q } : {}),
    from,
    to,
  };

  // Live count for the chosen window + filters, so the user knows what they'll get.
  const countQuery = useLeadsQuery(rangeError ? null : workspaceSlug, {
    ...exportParams,
    limit: 1,
    offset: 0,
  });
  const total = countQuery.data?.pagination.total;

  const onExport = () => {
    if (rangeError) return;
    triggerDownload(buildLeadsExportUrl(workspaceSlug, exportParams));
    onClose();
  };

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <header className="dialog-header">
          <h3>Export leads to CSV</h3>
          <button className="dialog-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="form-stack">
          <p className="muted" style={{ fontSize: 13 }}>
            Exports the leads <strong>created in the selected week</strong>, with the
            filters from your current view applied.
          </p>

          <div className="field">
            <span className="field-label">Created between</span>
            <div className="filter-date-row">
              <input
                type="date"
                value={from}
                max={to || undefined}
                onChange={(e) => setFrom(e.target.value)}
                aria-label="From date"
              />
              <span aria-hidden>→</span>
              <input
                type="date"
                value={to}
                min={from || undefined}
                max={from ? addDaysISO(from, MAX_RANGE_DAYS) : undefined}
                onChange={(e) => setTo(e.target.value)}
                aria-label="To date"
              />
            </div>
            {rangeError ? <span className="form-error">{rangeError}</span> : null}
          </div>

          <div className="field">
            <span className="field-label">Filters applied</span>
            {activeFilters.length === 0 ? (
              <p className="muted" style={{ fontSize: 13 }}>
                No filters — every lead in the selected week.
              </p>
            ) : (
              <div className="filter-chips" role="list">
                {activeFilters.map((c) => (
                  <span key={c.key} className="filter-chip" role="listitem">
                    <span className="filter-chip-label">{c.label}</span>
                    <span className="filter-chip-value">{c.value}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={onExport} disabled={!!rangeError}>
            {rangeError || total === undefined
              ? "Export CSV"
              : `Export ${total} lead${total === 1 ? "" : "s"}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
